import {
  ApiFiltrosDocumento,
  Departamento,
  Documento,
  FiltrosLegislation,
  FiltrosNotification,
  LegislationResponse,
  Notification,
  NotificationResponse,
  Seccion,
} from "../types";

const API_URL = "http://localhost:8000";

interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

export const fetchDocumentos = async (
  filtros: ApiFiltrosDocumento
): Promise<PaginatedResponse<Documento>> => {
  // Generar un ID único para esta llamada para rastreo
  const callId = Math.random().toString(36).substring(2, 9);

  try {
    console.log(
      `🔍 [${callId}] Iniciando fetchDocumentos con filtros:`,
      filtros
    );

    // Construir la URL con los parámetros de filtro
    const url = new URL(`${API_URL}/documentos/`);

    // Añadir parámetros de filtro a la URL
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        url.searchParams.append(key, value.toString());
      }
    });

    console.log(`🌐 [${callId}] Llamando a URL:`, url.toString());

    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), { headers });

    if (!response.ok) {
      console.error(
        `❌ [${callId}] Error en la respuesta:`,
        response.status,
        response.statusText
      );
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(
      `✅ [${callId}] Respuesta recibida con ${
        data.items?.length || 0
      } documentos. Total: ${data.total || 0}`
    );

    // Asumiendo que la API devuelve { items: Documento[], total: number }
    return {
      items: data.items || [],
      total: data.total || 0,
    };
  } catch (error) {
    console.error(`🚨 [${callId}] Error en fetchDocumentos:`, error);
    throw error;
  }
};

export const fetchSecciones = async (): Promise<Seccion[]> => {
  try {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/secciones/`, { headers });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching secciones:", error);
    throw error;
  }
};

export const fetchDepartamentos = async (): Promise<Departamento[]> => {
  try {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/departamentos/`, { headers });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching departamentos:", error);
    throw error;
  }
};

export const fetchTiposDocumento = async (): Promise<any[]> => {
  try {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/tipos-documento/`, {
      headers,
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching tipos de documento:", error);
    throw error;
  }
};

export const generarPdfPersonalizado = async (id: string): Promise<Blob> => {
  try {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(
      `${API_URL}/generar_pdf_con_hipervinculos?id=${id}`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.blob();
  } catch (error) {
    console.error("Error generando PDF con hipervínculos:", error);
    throw error;
  }
};

// Interfaces para las alertas
export interface UserAlert {
  id?: number;
  username: string;
  alert_name: string;
  seccion_codigo?: string;
  departamento_codigo?: string;
  tipo_documento?: string;
  busqueda?: string;
  texto?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  frequency?: string;
  is_active?: boolean;
  last_notified?: string;
  created_at?: string;
  seccion_nombre?: string;
  departamento_nombre?: string;
  // Nuevos campos para notificaciones
  alert_type?: string; // 'disposiciones' o 'notificaciones'
  departamento?: string; // Departamento para notificaciones
  subdepartamento?: string; // Subdepartamento para notificaciones
  search_text?: string; // Texto a buscar en notificaciones
}

// Crear una nueva alerta
export const createAlert = async (
  alertData: UserAlert
): Promise<{ alert_id: number }> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(alertData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating alert:", error);
    throw error;
  }
};

// Obtener todas las alertas del usuario
export const getUserAlerts = async (): Promise<UserAlert[]> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching user alerts:", error);
    throw error;
  }
};

// Obtener una alerta específica
export const getAlertById = async (alertId: number): Promise<UserAlert> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts/${alertId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching alert with ID ${alertId}:`, error);
    throw error;
  }
};

// Actualizar una alerta existente
export const updateAlert = async (
  alertId: number,
  alertData: UserAlert
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts/${alertId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(alertData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(`Error updating alert with ID ${alertId}:`, error);
    throw error;
  }
};

// Eliminar una alerta
export const deleteAlert = async (
  alertId: number
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts/${alertId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(`Error deleting alert with ID ${alertId}:`, error);
    throw error;
  }
};

// Activar o desactivar una alerta
export const toggleAlert = async (
  alertId: number
): Promise<{ message: string; is_active: boolean }> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/alerts/${alertId}/toggle`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(`Error toggling alert with ID ${alertId}:`, error);
    throw error;
  }
};

// Notifications API functions
export const fetchNotifications = async (
  filtros: FiltrosNotification
): Promise<NotificationResponse> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    // Construir la URL con los parámetros de filtro
    const url = new URL(`${API_URL}/notifications`);

    // Añadir parámetros de filtro a la URL
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        url.searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const fetchNotificationById = async (
  notificationId: number
): Promise<Notification> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/notifications/${notificationId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Error fetching notification with ID ${notificationId}:`,
      error
    );
    throw error;
  }
};

export const fetchNotificationDepartments = async (): Promise<string[]> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/notifications/departments`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notification departments:", error);
    throw error;
  }
};

export const fetchNotificationSubdepartments = async (
  departamento?: string
): Promise<string[]> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    // Construir la URL con el parámetro de departamento si existe
    const url = new URL(`${API_URL}/notifications/subdepartments`);
    if (departamento) {
      url.searchParams.append("departamento", departamento);
    }

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notification subdepartments:", error);
    throw error;
  }
};

export const fetchNotificationDates = async (): Promise<string[]> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_URL}/notifications/dates`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notification dates:", error);
    throw error;
  }
};

// Función para obtener legislación consolidada
export const fetchLegislation = async (
  filters: FiltrosLegislation = {}
): Promise<LegislationResponse> => {
  try {
    const queryParams = new URLSearchParams();

    // Añadir filtros a los parámetros de consulta
    if (filters.from_date) queryParams.append("from_date", filters.from_date);
    if (filters.to_date) queryParams.append("to_date", filters.to_date);
    if (filters.search_text)
      queryParams.append("search_text", filters.search_text);

    const response = await fetch(
      `${API_URL}/stats/boe/legislacion-consolidada?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // Asegúrate de que estás accediendo a la propiedad correcta
    return {
      items: data.data || [], // Cambia aquí para acceder a 'data'
      total: data.data.length || 0, // Total de elementos
      total_pages: Math.ceil(
        (data.data.length || 0) / (filters.limit ? parseInt(filters.limit) : 10)
      ), // Calcula las páginas
    };
  } catch (error) {
    console.error("Error fetching legislation:", error);
    throw error;
  }
};
